import log from "loglevel";
import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Box, IconButton} from "@mui/material";
import {Media} from "@twilio/conversations";
import {extension as mimeToExtension} from "mime-types";

import {Colors, Container, IconClose, IconDocument, Text} from "@bounce/shared/ui";
import {addNotification, detachFiles} from "../store/actions/genericActions";
import {AppState} from "../store/definitions";
import {notifications} from "../notifications";
import {roundFileSizeInMB} from "../utils/roundFileSizeInMB";

interface FilePreviewProps {
  file: File;
  isBubble: boolean;
  disabled?: boolean;
  media?: Media;
  focusable: boolean;
}

export const FilePreview = (props: FilePreviewProps) => {
  const {file, isBubble, disabled, media, focusable} = props;
  const [isHovered, setIsHovered] = useState(false);

  const dispatch = useDispatch();
  const fileAttachmentConfig = useSelector((state: AppState) => state.config.fileAttachment);

  const handleDetach = () => {
    dispatch(detachFiles([file]));
  };

  const handleDownload = async () => {
    if (fileAttachmentConfig?.maxFileSize && file.size > fileAttachmentConfig.maxFileSize) {
      dispatch(
        addNotification(
          notifications.fileDownloadInvalidSizeNotification({
            fileName: file.name,
            maxFileSize: `${roundFileSizeInMB(fileAttachmentConfig.maxFileSize)}MB`,
          })
        )
      );
      return;
    }

    if (
      fileAttachmentConfig?.acceptedExtensions &&
      !fileAttachmentConfig.acceptedExtensions.includes(mimeToExtension(file.type) as string)
    ) {
      dispatch(
        addNotification(
          notifications.fileDownloadInvalidTypeNotification({
            fileName: file.name,
          })
        )
      );
      return;
    }

    try {
      const url = media ? await media.getContentTemporaryUrl() : URL.createObjectURL(file);
      if (url) {
        window.open(url);
      }
    } catch (e) {
      log.error(`Failed downloading message attachment: ${e}`);
    }
  };

  const handleBoxClick = () => {
    if (!disabled) {
      handleDownload();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleBoxClick();
    }
  };

  return (
    <Box
      role="button"
      onClick={handleBoxClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onFocus={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onBlur={() => setIsHovered(false)}
      tabIndex={focusable ? 0 : -1}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
        py: "4px",
        px: "8px",
        mt: isBubble ? "8px" : 0,
        backgroundColor: Colors.bw.white,
        border: `1px solid ${Colors.grey._300}`,
        borderRadius: "4px",
        cursor: disabled ? "default" : "pointer",
        appearance: "none",
        width: "100%",
        "&:hover": disabled
          ? {}
          : {
              borderColor: Colors.grey._400,
            },
      }}
    >
      <IconDocument color={Colors.grey._600} size={16} />
      <Container
        sx={{
          alignItems: "flex-start",
          flex: 1,
          overflow: "hidden",
          gap: 0,
        }}
      >
        <Text
          size={10}
          fontWeight={"bold"}
          sx={{
            lineHeight: "14px",
            color: Colors.grey._900,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            textAlign: "left",
          }}
        >
          {file.name}
        </Text>
        <Text
          size={10}
          color={Colors.grey._600}
          sx={{
            lineHeight: "14px",
            minWidth: "70px",
            textAlign: "left",
          }}
        >
          {isHovered ? "Click to open" : `${roundFileSizeInMB(file.size)}MB`}
        </Text>
      </Container>
      {isBubble ? (
        <Container row sx={{color: Colors.grey._500, gap: 0}}>
          <IconClose />
        </Container>
      ) : (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleDetach();
          }}
        >
          <IconClose />
        </IconButton>
      )}
    </Box>
  );
};
